import { useCallback, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link as MuiLink, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { Link } from '@tanstack/react-router';
import { Trans } from 'react-i18next';

import { resendVerificationEmail } from '../lib/auth0-client';
import { useAuthTranslation } from '../i18n/auth-i18n';
import { AUTH_PATHS } from '../lib/auth-paths';
import { readQueryParam } from '../lib/query-params';
import { AuthLayout } from './AuthLayout';

export interface VerifyEmailPageProps {
  logo: React.ReactElement;
  brandingSubtitle: string;
}

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const ORCHID = '#C86AC6';
const SAPPHIRE = '#6271F8';

export function VerifyEmailPage({ logo, brandingSubtitle }: VerifyEmailPageProps) {
  const { t } = useAuthTranslation();
  const [email] = useState(() => readQueryParam('email'));
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleResend = useCallback(async () => {
    if (!email) return;
    setState('sending');
    try {
      await resendVerificationEmail(email);
      setState('sent');
    } catch {
      setState('error');
    }
  }, [email]);

  return (
    <AuthLayout logo={logo} brandingSubtitle={brandingSubtitle} data-testid="auth.verify-email-page">
      <Stack spacing={2.5} alignItems="center" textAlign="center">
        <Box
          aria-hidden
          sx={(theme) => ({
            width: 68,
            height: 68,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.primary',
            background: `radial-gradient(circle at 32% 28%, ${alpha(ORCHID, 0.4)}, ${alpha(SAPPHIRE, 0.22)})`,
            border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
            boxShadow: `0 8px 30px -10px ${alpha(ORCHID, 0.6)}`,
          })}
        >
          <Icon icon="eva:email-outline" width={32} height={32} />
        </Box>

        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            {t('auth.verifyEmail.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, maxWidth: 320 }}>
            {email ? (
              <Trans
                i18nKey="auth.verifyEmail.sentTo"
                values={{ email }}
                components={{
                  strong: (
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, wordBreak: 'break-word' }} />
                  ),
                }}
              />
            ) : (
              t('auth.verifyEmail.sentGeneric')
            )}
          </Typography>
        </Box>

        {state === 'sent' && (
          <Alert severity="success" sx={{ width: '100%' }}>
            {t('auth.verifyEmail.sentSuccess')}
          </Alert>
        )}
        {state === 'error' && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('auth.verifyEmail.resendError')}
          </Alert>
        )}

        <Button
          variant="outlined"
          fullWidth
          disabled={!email || state === 'sending'}
          onClick={handleResend}
          sx={{
            height: 44,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            color: '#F4F2F8', // hardcoded white — outlined buttons default to primary (violet)
            borderColor: 'divider',
            transition: `transform 160ms ${EASE_OUT}, border-color 200ms ${EASE_OUT}, background-color 200ms ${EASE_OUT}`,
            '&:hover': { borderColor: alpha('#FFFFFF', 0.3), backgroundColor: 'action.hover' },
            '&:active': { transform: 'scale(0.97)' },
            '&.Mui-disabled': { transform: 'none' },
          }}
        >
          {state === 'sending' ? <CircularProgress size={20} color="inherit" /> : t('auth.verifyEmail.resend')}
        </Button>

        <MuiLink
          component={Link}
          to={AUTH_PATHS.login}
          variant="body2"
          underline="hover"
          sx={{
            color: 'text.secondary',
            '@media (hover: hover)': { '&:hover': { color: 'text.primary' } },
            '&:active': { transform: 'scale(0.97)' },
          }}
        >
          {t('auth.common.backToSignIn')}
        </MuiLink>
      </Stack>
    </AuthLayout>
  );
}
